import React from 'react'
import { forwardRefType } from '../utils/forward-ref-type'
import { composeRefs } from '../utils/compose-refs'
import { useServerErrors } from './global-form-context'

export type HeadlessFormV2Props<As extends React.ElementType> = {
  id?: string
  children?: React.ReactNode
  as: RefType<As> extends HTMLFormElement ? As : never
  method?: 'get' | 'post' | 'put' | 'patch' | 'delete' | 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | undefined
  onSubmit?: React.SubmitEventHandler<HTMLFormElement>
} & React.ComponentProps<As>

type RefType<T extends React.ElementType<any>> = React.ComponentPropsWithRef<T>['ref'] extends React.Ref<infer I> | undefined
  ? I
  : never

function _HeadlessFormV2Root<As extends React.ElementType = 'form'>(
  { id: inputId, children, as, method, onSubmit, ...props }: HeadlessFormV2Props<As>,
  forwardedRef: React.Ref<HTMLFormElement>
) {
  const FormComponent = as ?? 'form'
  const generatedId = React.useId()
  const id = inputId ?? generatedId
  const formRef = React.useRef<HTMLFormElement>(null)

  const serverErrors = useServerErrors(id)
  const lastServerErrors = React.useRef(serverErrors)

  React.useEffect(() => {
    if (formRef?.current && lastServerErrors.current !== serverErrors) {
      lastServerErrors.current = serverErrors
      formRef.current?.checkValidity()
    }
  })

  const isGET = method === 'get' || method === 'GET' || !method

  return (
    <formContext.Provider value={{ id }}>
      <FormComponent
        {...props}
        id={id}
        method={method}
        ref={composeRefs(formRef, forwardedRef)}
        onSubmit={(e: React.SubmitEvent<HTMLFormElement>) => {
          if (isGET) {
            if (!formRef.current?.checkValidity()) {
              e.preventDefault()
              e.stopPropagation()
              return
            }
          }
          onSubmit?.(e)
        }}
      >
        {isGET ? null : <input type="hidden" name="__formId" value={id} />}
        {children}
      </FormComponent>
    </formContext.Provider>
  )
}

export const HeadlessFormV2Root = (React.forwardRef as forwardRefType)(_HeadlessFormV2Root)

interface FormContext {
  id: string
}

export const formContext = React.createContext<FormContext>({ id: '' })
